import React, { useEffect, useRef, useState, useCallback } from "react";
import CustomSpinner from "../../../../util/CustomSpinner";
import DataTableComponent, { ColumnOption } from "../../../common/DataTable";
import axios from "../../../../util/axiosconfig";
import { Toast } from "primereact/toast";
import { showError, showSuccess } from "../../../../util/ShowStatus";
import { ProgressSpinner } from "primereact/progressspinner";
import { ProgressBar } from "primereact/progressbar";
import { Error } from "../../../../util/types";
import { Button } from "primereact/button";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";

interface ExportHistory {
  id: number;
  file_name: string;
  status: string;
  file_location: string;
  created_at: string;
}

type FetchedForms = {
  id: number;
  file_name: string;
  status: string;
  file_location: string;
  created_at: string;
  import_name: string;
  import_type: string;
  processed_batches: number;
  total_batches: number;
  total_records: number;
};

interface JobsheetBelfastHistoryProps {
  toast: React.RefObject<Toast>;
}

const JobsheetBelfastHistory: React.FC<JobsheetBelfastHistoryProps> = ({
  toast,
}) => {
  const [forms, setForms] = useState<ExportHistory[]>([]);
  const [downloadFormExportId, setDownloadFormExportId] = useState<
    number | null
  >(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [firstTimeLoading, setFirstTimeLoading] = useState<boolean>(true);
  const [changePageLoading, setChangePageLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [size, setSize] = useState(10);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  let page = 0;

  const fetchFormHistory = useCallback(
    async (page = 0, size = 10) => {
      const params = { page, size, importType: "belfastJobSheet" }; // Add location parameter

      setLoading(true);
      try {
        const response = await axios.get("user/download-history", { params });
        const fetchedForms = response.data.result;
        setChangePageLoading(false);
        if (!fetchedForms) {
          showError(
            response?.data?.Message ??
              "Failed to fetch Belfast jobsheet history.",
            toast,
            null,
            8000
          );
        }
        setForms(fetchedForms);
        setTotalRecords(response.data.totalCount);
        setLoading(false);
        setChangePageLoading(false);
        setFirstTimeLoading(false);

        // const allCompleted = fetchedForms?.every(
        //   (form: FetchedForms) => form.status === "Completed"
        // );
        // if (allCompleted && intervalIdRef.current) {
        //   clearInterval(intervalIdRef.current);
        //   intervalIdRef.current = null;
        // }
        const isAnyInProgress = fetchedForms?.some(
          (form: FetchedForms) => form.status === "In Progress"
        );
        if (!isAnyInProgress && intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
      } catch (err: unknown) {
        const error = err as Error;
        showError(
          error?.response?.data?.Message ??
            "Failed to fetch Belfast jobsheet history.",
          toast
        );
        setLoading(false);
        setChangePageLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    fetchFormHistory(page, size);

    const startPolling = () => {
      if (!intervalIdRef.current) {
        intervalIdRef.current = setInterval(() => {
          fetchFormHistory(page, size);
        }, 5100);
      }
    };

    const stopPolling = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };

    startPolling();

    return () => {
      stopPolling();
    };
  }, [fetchFormHistory, page, size]);

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setChangePageLoading(true);
    setFirst(event.first);
    setSize(event.rows);
    const currentPage = Math.floor(event.first / event.rows);
    fetchFormHistory(currentPage, event.rows);

    page = currentPage;

    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    intervalIdRef.current = setInterval(() => {
      fetchFormHistory(currentPage, event.rows);
    }, 5100);
  };

  const handleDownload = async (form: FetchedForms) => {
    setIsButtonDisabled(true);
    setIsDownloading(true);
    setDownloadFormExportId(form.id);
    try {
      const url = form.file_location;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        form.file_location.split("/").pop() || "download.zip"
      );
      document.body.appendChild(link);
      link.click();
      setIsButtonDisabled(false);
      showSuccess(`${form.file_name} downloaded successfully.`, toast);
    } catch (err: unknown) {
      const error = err as Error;
      showError(
        error?.response?.data?.Message ?? "Failed to download form.",
        toast
      );
      setIsButtonDisabled(false);
    } finally {
      setIsButtonDisabled(false);
      setIsDownloading(false);
      setDownloadFormExportId(null);
    }
  };

  const downloadBtnBody = (form: FetchedForms) => {
    const isInProgress = form.status === "In Progress";
    return (
      <div className="tw-flex tw-items-center tw-justify-center">
        {isInProgress ? (
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-bg-amber-100 tw-rounded tw-text-amber-800 tw-text-sm">
            <ProgressBar
              mode="indeterminate"
              style={{ height: "4px", width: "60px" }}
              color="#f59e0b"
            />
            <span>Processing...</span>
          </div>
        ) : (
          <Button
            onClick={() => handleDownload(form)}
            disabled={isButtonDisabled || form.status === "Failed"}
            className={`tw-px-3 tw-py-1 tw-rounded focus:tw-shadow-none tw-bg-[#2a4870] tw-text-white hover:tw-bg-[#182c44]`}
            icon="pi pi-download"
            label="Download"
            // iconPos="left"
            // size="small"
          />
        )}
        {isDownloading && downloadFormExportId === form.id && (
          <div className="tw-ml-2">
            <CustomSpinner spinnerColour="tw-border-t-blue-600" />
          </div>
        )}
      </div>
    );
  };

  const exportHistoryColumns: ColumnOption[] = [
    {
      field: "file_name",
      header: "File Name",
      visible: true,
      body: (rowData: FetchedForms) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <i className="pi pi-file tw-text-blue-600"></i>
          <span className="tw-font-medium tw-text-gray-800">
            {rowData.file_name}
          </span>
        </div>
      ),
    },
    {
      field: "created_at",
      header: "Created At",
      body: (rowData: FetchedForms) => {
        const date = new Date(rowData.created_at);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        return (
          <div className="tw-flex tw-flex-col">
            <span className="tw-font-medium tw-text-gray-800">{`${day}-${month}-${year}`}</span>
            <span className="tw-text-sm tw-text-gray-500">{`${hours}:${minutes}:${seconds}`}</span>
          </div>
        );
      },
      visible: true,
    },
    {
      field: "status",
      header: "Status",
      body: (rowData: FetchedForms) => {
        const getStatusBadge = (status: string) => {
          switch (status.toLowerCase()) {
            case "completed":
              return (
                <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-800">
                  <i className="pi pi-check-circle tw-mr-1"></i>
                  Completed
                </span>
              );
            case "in progress":
              return (
                <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-amber-100 tw-text-amber-800">
                  <i className="pi pi-clock tw-mr-1"></i>
                  In Progress
                </span>
              );
            case "failed":
              return (
                <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-red-100 tw-text-red-800">
                  <i className="pi pi-times-circle tw-mr-1"></i>
                  Failed
                </span>
              );
            default:
              return (
                <span className="tw-inline-flex tw-items-center tw-px-3 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-800">
                  {status}
                </span>
              );
          }
        };
        return getStatusBadge(rowData.status);
      },
      visible: true,
    },
    {
      field: "downloadedBtn",
      header: "Actions",
      body: downloadBtnBody,
      visible: true,
    },
  ];

  return (
    <div>
      {changePageLoading && (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-20 tw-flex tw-justify-center tw-items-center tw-z-50">
          <div className="tw-bg-white tw-rounded-lg tw-p-6 tw-shadow-xl">
            <ProgressSpinner
              style={{ width: "40px", height: "40px" }}
              strokeWidth="4"
            />
            <p className="tw-mt-3 tw-text-sm tw-text-gray-600 tw-font-medium">
              Loading...
            </p>
          </div>
        </div>
      )}

      {loading && firstTimeLoading ? (
        <div className="tw-flex tw-justify-center tw-items-center tw-py-20">
          <div className="tw-text-center">
            <ProgressSpinner
              style={{ width: "50px", height: "50px" }}
              strokeWidth="4"
            />
            <p className="tw-mt-4 tw-text-gray-600 tw-font-medium">
              Loading Belfast jobsheet history...
            </p>
          </div>
        </div>
      ) : (
        <div className="tw-space-y-4">
          {/* Table Header */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            {/* <div>
              <h2 className="tw-text-xl tw-font-semibold tw-text-gray-800">
                Belfast Jobsheets
              </h2>
              <p className="tw-text-sm tw-text-gray-600">
                Total: {totalRecords} records
              </p>
            </div> */}
            {/* <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500">
              <i className="pi pi-info-circle"></i>
              <span>Auto-refreshes every 3 seconds</span>
            </div> */}
          </div>

          {/* Data Table */}
          <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 tw-overflow-hidden">
            <DataTableComponent
              rowData={forms}
              columns={exportHistoryColumns}
            />
          </div>

          {/* Pagination */}
          <div className="">
            <Paginator
              first={first}
              rows={size}
              totalRecords={totalRecords}
              rowsPerPageOptions={[5, 10, 25, 50]}
              onPageChange={onPageChange}
              template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              className="tw-border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsheetBelfastHistory;
